import React, {ReactNode} from 'react';
import {Card, CardActions, CardContent, CardHeader, TypographyProps} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {themeColors} from '../../theme/Theme';

interface PanelDefinitionListComponentProps {
  content: {
    dd: string;
    dt: string;
  }[];
}

const useListStyles = makeStyles((theme) => ({
  root: {
    margin: `-${theme.spacing(2)} 0 0`,
  },
  dd: {
    margin: 0,
  },
  dt: {
    color: themeColors.tanMuted,
    width: '25%',
  },
  row: {
    borderBottom: `1px solid ${themeColors.backgroundDark}`,
    display: 'flex',
    flexWrap: 'wrap',
    padding: `${theme.spacing(2)} 0`,
  },
}));

export function PanelDefinitionListComponent({
  content,
}: PanelDefinitionListComponentProps) {
  const classes = useListStyles();

  return (
    <dl className={classes.root}>
      {content.map(({dd, dt}) => (
        <div className={classes.row} key={dt}>
          <dt className={classes.dt}>{dt}</dt>
          <dd className={classes.dd}>{dd}</dd>
        </div>
      ))}
    </dl>
  );
}

interface PanelFooterProps {
  children: ReactNode;
}

export function PanelFooter({children}: PanelFooterProps) {
  return <CardActions>{children}</CardActions>;
}

interface PanelTitleProps {
  children: ReactNode;
  variant?: TypographyProps['variant'];
}

const useTitleStyles = makeStyles((theme) => ({
  root: {
    color: themeColors.white,
    borderBottom: `1px solid ${themeColors.backgroundDark}`,
    fontSize: 28,
    padding: `${theme.spacing(2)} 0 ${theme.spacing(3)}`,
    margin: `0 0 ${theme.spacing(2)}`,
    '& .MuiTypography-root': {
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
    },
  },
}));

export function PanelTitle({children, variant = 'h2'}: PanelTitleProps) {
  const classes = useTitleStyles();
  return (
    <CardHeader
      className={classes.root}
      title={children}
      titleTypographyProps={{
        variant,
      }}
    />
  );
}

interface MfaPanelProps {
  children: ReactNode;
  footer?: string;
  title?: string;
}

const panelClasses = makeStyles((theme) => ({
  root: {
    background: themeColors.backgroundHighlight,
    margin: `0 0 ${theme.spacing(2)}`,
    padding: `${theme.spacing(3)} ${theme.spacing(7.5)} ${theme.spacing(5)}`,
  },
  content: {
    color: themeColors.white,
    padding: 0,
  },
}));

function MfaPanel({children, footer, title}: MfaPanelProps) {
  const classes = panelClasses();

  return (
    <Card className={classes.root}>
      {title && <PanelTitle>{title}</PanelTitle>}

      <CardContent className={classes.content}>{children}</CardContent>

      {footer && <PanelFooter>{footer}</PanelFooter>}
    </Card>
  );
}

export default MfaPanel;
